import { WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { type ClipboardScoreProfileResponse } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { openZendeskMessaging } from "@src/appV2/lib/ZendeskMessaging/openZendeskMessaging";
import { Callout, CbhIcon, Text } from "@src/appV2/redesign/system/ui-components";
import { parseISO } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { formatInTimeZone } from "date-fns-tz";

import { Button } from "../../../components/Button";
import { isScoreHealthy } from "../../utils/utils";
import { RankingLabel } from "../RankingLabel";

interface ClipboardScoreHeaderProps {
  score: number;
  ranking: ClipboardScoreProfileResponse["clipboardScoreRank"];
  qualification: string;
  accountStatus: ClipboardScoreProfileResponse["accountStatus"];
  reEnrollmentScore: number;
  tmz: string;
}

function getTitleAndSubtitle(
  score: number,
  accountStatus: ClipboardScoreProfileResponse["accountStatus"],
  tmz: string,
  reEnrollmentScore: number
): { title?: string; subtitle?: string } {
  if (accountStatus.status === WorkerStage.RESTRICTED) {
    return {
      title:
        accountStatus.reEnrollmentDate &&
        `Lifts on ${formatInTimeZone(
          parseISO(accountStatus.reEnrollmentDate),
          tmz,
          "MMM d, yyy h:mm aa"
        )}`,
      subtitle: `Afterwards, your score will reset to ${reEnrollmentScore}.`,
    };
  }

  return {
    title: isScoreHealthy(score)
      ? "Your Clipboard Score is healthy. Keep it up!"
      : "Your Clipboard Score is low. Show up and arrive on time to build it back!",
  };
}

export function ClipboardScoreHeader(props: ClipboardScoreHeaderProps) {
  const {
    score,
    ranking: { rank, outOf } = {},
    accountStatus,
    qualification,
    tmz,
    reEnrollmentScore,
  } = props;

  const { title, subtitle } = getTitleAndSubtitle(score, accountStatus, tmz, reEnrollmentScore);

  return (
    <Box>
      <Stack spacing={6} justifyContent="center" alignItems="center" padding={10} marginBottom={6}>
        <Stack spacing={6} justifyContent="center" alignItems="center" paddingX={7}>
          <Stack spacing={2} textAlign="center">
            {isDefined(title) && <Text variant="h5">{title}</Text>}
            {isDefined(subtitle) && (
              <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
                {subtitle}
              </Text>
            )}
          </Stack>

          <Stack justifyContent="center" alignSelf="stretch" textAlign="center">
            {accountStatus.status === WorkerStage.RESTRICTED ? (
              <Text color={(theme) => theme.palette.error.main} sx={{ fontSize: "48px" }}>
                Restricted
              </Text>
            ) : (
              <Text sx={{ fontSize: "100px" }}>{score}</Text>
            )}
          </Stack>
        </Stack>

        {accountStatus.status === WorkerStage.RESTRICTED ? (
          <Button
            invert
            onClick={() => {
              void openZendeskMessaging();
            }}
          >
            Have questions? Reach out
            <CbhIcon size="small" type="chevron-right" />
          </Button>
        ) : (
          isDefined(rank) &&
          isDefined(outOf) && (
            <Callout
              iconType={
                isScoreHealthy(score)
                  ? "clipboard-score-healthy-colored"
                  : "clipboard-score-unhealthy-colored"
              }
              title={<RankingLabel rank={rank} outOf={outOf} qualification={qualification} />}
              sx={{
                background: (theme) => theme.palette.background.paper,
              }}
            />
          )
        )}
      </Stack>
    </Box>
  );
}
